/** The Super Admin (docs/specs/super-admin.md): the platform owner's console. English is the source language. */
export const superAdmin = {
    appName: "Sobthik Super Admin",
    badge: "Super Admin",
    signIn: {
        title: "Super Admin sign-in",
        description: "Platform admins only. Your password, and the authenticator code if two-step sign-in is on.",
        identifier: "Email or phone",
        password: "Password",
        code: "Authenticator code",
        codeHint: "Only if two-step sign-in is on for your account.",
        submit: "Sign in",
    },
    nav: {
        overview: "Overview",
        tenants: "Businesses",
        payments: "Payments",
        operations: "Operations",
        announcements: "Announcements",
        plans: "Plans",
        closures: "Closures",
        feedback: "Feedback",
        leads: "Sales enquiries",
        domains: "Shop domains",
        storefronts: "Online stores",
        settings: "Settings",
        themeDemos: "Theme demo shops",
    },
    setup: {
        title: "Create the first Super Admin",
        description: "No Super Admin exists yet. The account you create here owns the platform.",
        name: "Your name",
        email: "Email",
        phone: "Mobile number (optional)",
        password: "Password",
        passwordHint: "At least 8 characters.",
        submit: "Create and sign in",
    },
    settings: {
        title: "Settings",
        description: "Platform settings take effect at once, without a deploy.",
        ownerOnly: "Only a platform owner can change these settings.",
        signupTitle: "Merchant signup",
        signupBody: "Where new merchants get their signup code.",
        bySms: "Code by SMS (the mobile number is verified)",
        byEmail: "Code by e-mail (the mobile number is not verified yet)",
        noSms: "No SMS gateway is connected: with SMS codes, nobody can sign up.",
        totpTitle: "Super Admin sign-in",
        totpRequired: "Require two-step sign-in for every Super Admin",
        totpHint: "Off: the password is enough, unless an admin turned two-step sign-in on for themselves.",
        saved: "Settings saved",
    },
    billingSettings: {
        title: "Billing settings",
        description: "Who is named on invoices, where businesses pay you, VAT and the payment timeline.",
        seller: "Invoice seller",
        sellerName: "Seller name",
        sellerAddress: "Seller address",
        sellerBin: "BIN",
        binHint: "13 digits, or empty.",
        vat: "VAT (%)",
        notifyEmail: "E-mail for new payment requests",
        bkash: "bKash for plan payments",
        bkashNumber: "bKash number",
        bkashType: "Account type",
        types: { personal: "Personal", agent: "Agent", merchant: "Merchant" },
        timeline: "Payment timeline (days)",
        invoiceDueDays: "Invoice due after",
        reminderDays: "Reminders before the due date",
        reminderHint: "Days, separated by commas: 7, 3, 1",
        graceDays: "Grace period",
        restrictedDays: "Restricted for",
        retentionDays: "Keep data after suspension",
        warnBeforeDays: "Warn before restricting",
        invalid: "VAT and the day fields must be numbers.",
        confirmTitle: "Save billing settings",
        confirmBody: "New invoices and payment pages use these at once. Invoices already issued do not change.",
        saved: "Billing settings saved",
    },
    security: {
        title: "My two-step sign-in",
        on: "Two-step sign-in is on for your account.",
        off: "Two-step sign-in is off for your account.",
        turnOn: "Turn on",
        turnOff: "Turn off",
        requiredLocked: "Required for every Super Admin, so it cannot be turned off.",
        enrolTitle: "Turn on two-step sign-in to continue",
        enrolBody: "This platform requires two-step sign-in for every Super Admin.",
        manual: "Add an account in your authenticator app (Google Authenticator, Authy, …) and type this setup key:",
    },
    newBusiness: {
        open: "New business",
        title: "Create a business",
        description: "The owner signs in with the temporary password, then chooses their own and accepts the Terms.",
        businessName: "Business name",
        businessType: "How they sell",
        plannedBranches: "Branches",
        ownerName: "Owner's name",
        email: "Owner's email",
        phone: "Owner's mobile number",
        temporaryPassword: "Temporary password (empty: we make one)",
        submit: "Create business",
        createdTitle: "Business created",
        createdBody: "Give this temporary password to the owner. It is shown only now; they must change it at first sign-in.",
        copy: "Copy",
        copied: "Copied",
        openBusiness: "Open the business",
    },
    reason: {
        label: "Reason",
        hint: "Kept in the platform audit log.",
    },
    status: {
        trial: "Trial",
        active: "Active",
        restricted: "Restricted",
        suspended: "Suspended",
        closed: "Closed",
    },
    overview: {
        title: "Overview",
        description: "The platform in numbers.",
        mrr: "Monthly recurring revenue",
        active: "Active businesses",
        trial: "On trial",
        atRisk: "Restricted or suspended",
        conversion: "Trial to paid",
        months: "Last 12 months",
        month: "Month",
        signups: "Signups",
        firstPayments: "First payments",
        churned: "Churned",
    },
    tenants: {
        title: "Businesses",
        description: "Every business on the platform.",
        search: "Search by name or slug",
        allStatuses: "All statuses",
        name: "Business",
        plan: "Plan",
        status: "Status",
        mrr: "MRR",
        created: "Signed up",
        lastActivity: "Last activity",
        never: "Never",
        empty: "No businesses match.",
    },
    tenant: {
        facts: "Account",
        trialEnds: "Trial ends",
        dueAt: "Payment due",
        stage: "Payment stage",
        usage: "Usage",
        limit: "{{usage}} of {{limit}}",
        unlimited: "{{usage}} (no limit)",
        flags: "Feature flags",
        noFlags: "No feature flags yet.",
        overrides: "Overrides",
        noOverrides: "No overrides.",
        notes: "Internal notes",
        noNotes: "No notes yet.",
        support: "Support access",
        noSupport: "The owner has not given support access.",
        supportUntil: "Granted until {{date}}",
        invoices: "Invoices",
        noInvoices: "No invoices yet.",
        on: "On",
        off: "Off",
        actions: {
            plan: "Change plan",
            extendTrial: "Extend trial",
            suspend: "Suspend",
            reactivate: "Reactivate",
            note: "Add note",
            override: "Set override",
            flagOn: "Turn on",
            flagOff: "Turn off",
            openSupport: "Open as support",
        },
        dialogs: {
            plan: "Move this business to another plan",
            extendTrial: "Give this business more trial days",
            days: "Days",
            suspend: "Suspend this business",
            suspendBody: "Staff can only reach billing until you reactivate it. POS tills keep selling.",
            reactivate: "Lift the suspension or restriction",
            note: "A note for the platform team (the business never sees it)",
            noteBody: "Note",
            override: "Override a plan value for this business",
            key: "Value",
            value: "New value (empty = unlimited)",
            flag: "Change a feature flag",
            openSupport: "Open this business as support",
            supportOpened: "Support session open until {{date}}. Paste the token into the Admin's support sign-in (/support-sign-in).",
            copyToken: "Copy token",
            copied: "Copied",
        },
        done: "Saved",
    },
    payments: {
        title: "Payments",
        description: "Payments waiting for approval, oldest first. Check the bKash statement or the cash before approving.",
        empty: "No payments waiting.",
        business: "Business",
        invoice: "Invoice",
        amount: "Amount",
        how: "How",
        waiting: "Waiting",
        hours: "{{hours}} h",
        late: "Late",
        cash: "Cash",
        bkash: "bKash {{trxId}} from {{phone}}",
        approve: "Approve",
        reject: "Reject",
        approveTitle: "The money arrived: activate the plan",
        note: "Note (optional)",
        rejectTitle: "Not received or not right: tell the business why",
        approved: "Approved: the plan is active.",
        rejected: "Rejected: the business has been told.",
    },
    operations: {
        title: "Operations",
        description: "Queues, failed jobs, POS reviews and health.",
        health: "Health",
        database: "Database",
        redis: "Valkey",
        backups: "Backups",
        up: "Up",
        down: "Down",
        notConfigured: "Not configured",
        queues: "Queues",
        queue: "Queue",
        waiting: "Waiting",
        active: "Active",
        delayed: "Delayed",
        failed: "Failed",
        failedJobs: "Failed jobs",
        noFailed: "No failed jobs.",
        job: "Job",
        error: "Error",
        attempts: "Attempts",
        when: "When",
        retry: "Retry",
        retryTitle: "Run this job again",
        retried: "The job is queued again.",
        reviews: "POS sync reviews open",
        noReviews: "No open reviews.",
        open: "Open",
    },
    announcements: {
        title: "Announcements",
        description: "Messages shown at the top of the Admin.",
        create: "New announcement",
        titleEn: "Title (English)",
        titleBn: "Title (Bengali)",
        bodyEn: "Text (English)",
        bodyBn: "Text (Bengali)",
        level: "Level",
        info: "Information",
        warning: "Warning",
        audience: "Who sees it",
        everyone: "Every business",
        chosen: "Chosen businesses",
        tenantIds: "Business IDs (one per line)",
        endsAt: "Ends (optional)",
        publish: "Publish",
        end: "End now",
        ended: "Ended",
        showing: "Showing",
        scheduled: "Scheduled",
        empty: "No announcements yet.",
        published: "Published.",
    },
    plans: {
        title: "Plans",
        description: "Plans, their features and prices. Change anything here without a deploy; every change asks for a reason.",
        monthly: "Monthly",
        yearly: "Yearly",
        noPrice: "No price",
        changePrice: "Change price",
        priceTitle: "Change the {{plan}} price",
        cycle: "Cycle",
        price: "Price",
        retired: "Retired",
        default: "Default",
        hidden: "Not public",
        unlimited: "Unlimited",
        saved: "Price saved.",
        tabs: { plans: "Plans", grid: "Compare & edit", features: "Feature list", addons: "Add-ons" },
        perMonth: "/ month",
        perYear: "/ year",
        businesses_one: "{{count}} business",
        businesses_other: "{{count}} businesses",
        highlighted: "Recommended",
        newPlan: "New plan",
        duplicate: "Duplicate",
        editDetails: "Edit details",
        newTitle: "New plan",
        duplicateTitle: "Duplicate {{plan}}",
        newHint: "A new plan starts hidden from the pricing page. Set its values and prices, then make it public.",
        key: "Key",
        keyHint: "Small letters, digits and _; it never changes.",
        nameEn: "Name (English)",
        nameBn: "Name (Bengali)",
        created: "Plan created. It is hidden until you make it public.",
        detailsTitle: "Edit {{plan}}",
        taglineEn: "Tagline (English)",
        taglineBn: "Tagline (Bengali)",
        badgeEn: "Badge (English)",
        badgeBn: "Badge (Bengali)",
        badgeHint: "Empty with “Recommended” on shows “Most popular”.",
        sortOrder: "Order",
        trialDays: "Trial days (default plan)",
        isPublic: "Show on the pricing page and in billing",
        isHighlighted: "Recommended (drawn highlighted)",
        makeDefault: "Default plan for new businesses (trials)",
        status: "Status",
        statusActive: "Active",
        statusRetired: "Retired (no new businesses)",
        detailsSaved: "Plan saved.",
        feature: "Feature",
        notChecked: "Not checked yet",
        notCheckedHint: "Shown on plans, but the app does not enforce it yet.",
        display: "Shown only",
        on: "Included",
        off: "Not included",
        note: "Note",
        noteTitle: "Note for {{feature}} on {{plan}}",
        noteEn: "Note (English)",
        noteBn: "Note (Bengali)",
        noteHint: "Shown next to the value, e.g. “up to 3 branches”.",
        changed_one: "{{count}} change",
        changed_other: "{{count}} changes",
        discard: "Discard",
        saveChanges: "Save changes",
        saveTitle: "Save changes to {{plan}}",
        saveNext: "Save and go to the next plan",
        impactLoading: "Checking who is affected…",
        impactBusinesses_one: "{{count}} business is on this plan.",
        impactBusinesses_other: "{{count}} businesses are on this plan.",
        impactTrials_one: "{{count}} trial uses it as the default plan.",
        impactTrials_other: "{{count}} trials use it as the default plan.",
        smaller: "Smaller",
        overLimit: "{{businesses}} businesses and {{trials}} trials already use more. Nothing is deleted; they only cannot add more.",
        mode: "Businesses already on this plan",
        keepExisting: "Keep their current terms (recommended)",
        keepExistingHint: "They keep the old value of anything that gets smaller until they change plan. Trials get the new values.",
        applyToAll: "Apply to everyone now",
        applyToAllHint: "Every business on the plan gets the new values at once.",
        valuesSaved: "Plan values saved.",
        groups: {
            usage: "Limits and credits",
            channels: "Sales channels",
            stock: "Stock and operations",
            delivery: "Delivery",
            payments: "Payments",
            customers: "Customers",
            accounts_reports: "Accounts and reports",
            team: "Team",
            integrations: "Integrations",
            support: "Support",
        },
        kinds: { limit: "Limit", credit: "Credit", feature: "Feature", display: "Shown only" },
        addLine: "Add a line",
        addLineTitle: "Add a line to the plans",
        addLineHint: "A line only shown on plans, e.g. “WhatsApp support”. The app never checks it.",
        lineKey: "Key",
        lineKeyHint: "Starts with display., e.g. display.whatsapp_support",
        group: "Group",
        descriptionEn: "Description (English)",
        descriptionBn: "Description (Bengali)",
        unitEn: "Unit (English)",
        unitBn: "Unit (Bengali)",
        showOnPricing: "Show on the pricing page",
        archive: "Archive",
        archived: "Archived",
        restore: "Restore",
        edit: "Edit",
        editLineTitle: "Edit “{{feature}}”",
        moveUp: "Move up",
        moveDown: "Move down",
        pricingHidden: "Hidden from pricing",
        lineSaved: "Feature list saved.",
        empty: "No plans yet.",
        addons: {
            names: { locations: "Extra locations", devices: "Extra POS devices", sms: "SMS pack" },
            onSale: "On sale",
            off: "Not on sale",
            perMonth: "{{price}} a month per {{unit}}",
            perPack: "{{price}} per pack of {{unit}}",
            recurringHint: "Runs until the business's current period ends; prorated when bought mid-period.",
            packHint: "One-off credits that never expire.",
            editTitle: "Edit {{name}}",
            unit: "Pack size",
            unitHint: "How much one pack adds (e.g. 1 location, 1,000 SMS).",
            monthlyPrice: "Price per pack per month (yearly plans pay 12 ×)",
            packPrice: "Price per pack",
            active: "On sale to businesses",
            saved: "Add-on saved.",
        },
        keptTitle: "Kept plan terms",
        keptDescription: "Values this business kept when its plan was made smaller. They apply while it stays on that plan.",
        keptClear: "Move to current plan values",
        keptClearTitle: "Move {{business}} to the plan's current values?",
        keptCleared: "The business now has the plan's current values.",
    },
    closures: {
        title: "Closures",
        description: "Businesses that asked to close (deleted automatically when their 30 days end) and the deletion certificates of deleted ones.",
        closing: "Closing",
        deleted: "Deleted",
        noneClosing: "No business is closing.",
        noneDeleted: "No business has been deleted.",
        business: "Business",
        requested: "Requested",
        due: "Deleted on",
        reason: "Their reason",
        deletedAt: "Deleted at",
        tables: "Tables",
        rows: "Rows",
        cancel: "Cancel closure",
        cancelTitle: "Keep this business open?",
        cancelled: "The closure was cancelled.",
    },
    themeDemos: {
        title: "Theme demo shops",
        description: "One demo shop for every storefront theme: the Admin's “Live demo” opens it, so a merchant can see a theme before choosing it. Demo shops take no orders.",
        build_one: "Build {{count}} missing shop",
        build_other: "Build {{count}} missing shops",
        building: "Building…",
        queued: "Building in the background. Each shop takes a minute or two.",
        alreadyRunning: "A build is already running.",
        empty: "No themes.",
        theme: "Theme",
        address: "Address",
        status: "Status",
        statuses: { ready: "Live", missing: "Not built", closed: "Closed", taken: "Address taken by a business" },
    },
    storefronts: {
        title: "Online stores",
        description: "Businesses with an online store. Take one down for phishing or abuse; it stays closed until you restore it.",
        empty: "No online stores yet.",
        business: "Business",
        address: "Address",
        status: "Status",
        orders: "Online orders (30 days)",
        live: "On",
        off: "Off",
        takenDown: "Taken down",
        takeDown: "Take down",
        restore: "Restore",
        takeDownTitle: "Take down {{name}}'s shop?",
        takeDownBody: "The shop stops opening at once on every address. The business sees why and cannot reopen it.",
        restoreTitle: "Restore {{name}}'s shop?",
        restoreBody: "The shop opens again if the business has it turned on.",
        takenDownDone: "Shop taken down.",
        restored: "Shop restored.",
    },
    domains: {
        title: "Shop domains",
        description: "Own domains connected to online stores. Block one that is used for phishing or abuse.",
        all: "All",
        search: "Search host name",
        empty: "No domains.",
        host: "Domain",
        business: "Business",
        status: "Status",
        since: "Added",
        block: "Block",
        blockTitle: "Block {{host}}?",
        blockBody: "The shop stops opening on this domain at once and the business cannot remove or reconnect it.",
        blocked: "Domain blocked.",
        statuses: {
            pending_dns: "Waiting for DNS",
            verifying: "Checking",
            active: "Live",
            failed: "Not working",
            blocked: "Blocked",
        },
    },
    feedback: {
        title: "Feedback",
        description: "What beta shops sent from the Admin. Triage weekly: set a status, answer the merchant, keep a note.",
        all: "All",
        empty: "Nothing here.",
        business: "Business",
        sent: "Sent",
        kind: "Kind",
        message: "Message",
        status: "Status",
        triage: "Triage",
        triageTitle: "Triage feedback",
        response: "Answer to the merchant (optional)",
        note: "Internal note",
        saved: "Saved.",
        kinds: { bug: "Bug", idea: "Idea", question: "Question" },
        statuses: { new: "New", triaged: "Triaged", planned: "Planned", fixed: "Fixed", wont_fix: "Won't fix" },
    },
    leads: {
        title: "Sales enquiries",
        description: "Enquiries sent from the marketing site's Enterprise page, newest first. Call back within a day.",
        empty: "No enquiries yet.",
        business: "Business",
        contact: "Contact",
        branches: "Branches",
        message: "Message",
        received: "Received",
    },
};
//# sourceMappingURL=super-admin.js.map